import React, { useState } from 'react';
import { TicketIcon, MessageCircleIcon, PhoneIcon, MailIcon, ClockIcon, CheckCircleIcon, XCircleIcon, ChevronDownIcon } from 'lucide-react';
type Ticket = {
  id: string;
  subject: string;
  description: string;
  status: 'open' | 'closed';
  date: string;
  lastUpdate: string;
};
export const HelpSupport = () => {
  const [activeTab, setActiveTab] = useState('submit');
  const [tickets] = useState<Ticket[]>([{
    id: 'TK-001',
    subject: 'API Integration Issue',
    description: 'Having trouble connecting to the API endpoint...',
    status: 'open',
    date: '2023-06-15',
    lastUpdate: '2023-06-16'
  }, {
    id: 'TK-002',
    subject: 'Billing Question',
    description: "Need clarification about last month's charges...",
    status: 'closed',
    date: '2023-06-10',
    lastUpdate: '2023-06-12'
  }]);
  return <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8" data-id="element-1013">
      <div className="max-w-4xl mx-auto" data-id="element-1014">
        <h1 className="text-2xl font-bold mb-8" data-id="element-1015">Help & Support</h1>
        {/* Tab Navigation */}
        <div className="flex space-x-4 mb-8" data-id="element-1016">
          <button onClick={() => setActiveTab('submit')} className={`px-4 py-2 rounded-lg ${activeTab === 'submit' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`} data-id="element-1017">
            Submit Ticket
          </button>
          <button onClick={() => setActiveTab('history')} className={`px-4 py-2 rounded-lg ${activeTab === 'history' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`} data-id="element-1018">
            Ticket History
          </button>
          <button onClick={() => setActiveTab('contact')} className={`px-4 py-2 rounded-lg ${activeTab === 'contact' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`} data-id="element-1019">
            Contact
          </button>
        </div>
        {/* Submit Ticket Form */}
        {activeTab === 'submit' && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1020">
            <h2 className="text-xl font-semibold mb-6" data-id="element-1021">
              Submit a Support Ticket
            </h2>
            <form className="space-y-6" data-id="element-1022">
              <div data-id="element-1023">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1024">
                  Subject
                </label>
                <input type="text" className="w-full border-gray-200 rounded-lg" placeholder="Brief description of your issue" data-id="element-1025" />
              </div>
              <div data-id="element-1026">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1027">
                  Category
                </label>
                <select className="w-full border-gray-200 rounded-lg" data-id="element-1028">
                  <option data-id="element-1029">Technical Support</option>
                  <option data-id="element-1030">Billing</option>
                  <option data-id="element-1031">Account Issues</option>
                  <option data-id="element-1032">Feature Request</option>
                </select>
              </div>
              <div data-id="element-1033">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1034">
                  Description
                </label>
                <textarea className="w-full border-gray-200 rounded-lg" rows={6} placeholder="Please provide as much detail as possible..." data-id="element-1035" />
              </div>
              <button type="submit" className="w-full sm:w-auto px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700" data-id="element-1036">
                Submit Ticket
              </button>
            </form>
          </div>}
        {/* Ticket History */}
        {activeTab === 'history' && <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-1037">
            <div className="divide-y divide-gray-200" data-id="element-1038">
              {tickets.map(ticket => <div key={ticket.id} className="p-6" data-id="element-1039">
                  <div className="flex items-center justify-between mb-4" data-id="element-1040">
                    <div className="flex items-center space-x-4" data-id="element-1041">
                      <span className="text-sm font-medium text-gray-500" data-id="element-1042">
                        {ticket.id}
                      </span>
                      <h3 className="font-medium" data-id="element-1043">{ticket.subject}</h3>
                      <span className={`px-2 py-1 text-xs rounded-full ${ticket.status === 'open' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-1044">
                        {ticket.status}
                      </span>
                    </div>
                    <ChevronDownIcon className="h-5 w-5 text-gray-400" data-id="element-1045" />
                  </div>
                  <p className="text-gray-600 text-sm mb-4" data-id="element-1046">
                    {ticket.description}
                  </p>
                  <div className="flex items-center text-sm text-gray-500" data-id="element-1047">
                    <ClockIcon className="h-4 w-4 mr-1" data-id="element-1048" />
                    <span data-id="element-1049">Created: {ticket.date}</span>
                    <span className="mx-2" data-id="element-1050">•</span>
                    <span data-id="element-1051">Last Update: {ticket.lastUpdate}</span>
                  </div>
                </div>)}
            </div>
          </div>}
        {/* Contact Information */}
        {activeTab === 'contact' && <div className="space-y-6" data-id="element-1052">
            <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1053">
              <h2 className="text-xl font-semibold mb-6" data-id="element-1054">
                Contact Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1055">
                <div className="flex items-center space-x-4" data-id="element-1056">
                  <div className="flex-shrink-0 w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center" data-id="element-1057">
                    <MessageCircleIcon className="h-5 w-5 text-indigo-600" data-id="element-1058" />
                  </div>
                  <div data-id="element-1059">
                    <h3 className="font-medium" data-id="element-1060">Live Chat</h3>
                    <p className="text-sm text-gray-500" data-id="element-1061">Available 24/7</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4" data-id="element-1062">
                  <div className="flex-shrink-0 w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center" data-id="element-1063">
                    <PhoneIcon className="h-5 w-5 text-indigo-600" data-id="element-1064" />
                  </div>
                  <div data-id="element-1065">
                    <h3 className="font-medium" data-id="element-1066">Phone Support</h3>
                    <p className="text-sm text-gray-500" data-id="element-1067">+1 (555) 123-4567</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4" data-id="element-1068">
                  <div className="flex-shrink-0 w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center" data-id="element-1069">
                    <MailIcon className="h-5 w-5 text-indigo-600" data-id="element-1070" />
                  </div>
                  <div data-id="element-1071">
                    <h3 className="font-medium" data-id="element-1072">Email</h3>
                    <p className="text-sm text-gray-500" data-id="element-1073">
                      support@realuseai.com
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-6" data-id="element-1074">
              <h3 className="font-medium text-indigo-900 mb-2" data-id="element-1075">
                Business Hours
              </h3>
              <p className="text-sm text-indigo-700" data-id="element-1076">
                Monday - Friday: 9:00 AM - 6:00 PM EST
                <br data-id="element-1077" />
                Saturday: 10:00 AM - 4:00 PM EST
                <br data-id="element-1078" />
                Sunday: Closed
              </p>
            </div>
          </div>}
      </div>
    </div>;
};